import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router";
import axios from "axios";

import { LoaderContext } from "../../context/LoaderContext";
import { __API_URL__ } from "../../SERVER_URL";
import useFetch from "../../hooks/useFetch";
import useUser from "../../hooks/user";
import { useQueryClient } from "@tanstack/react-query";
import { USER_ROLES } from "../../utils/constants";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const STAFF_LABELS = {
  Inv_coordinator: "Investigator Coordinator",
  Cro_coordinator: "CRO Coordinator",
  "Data Manager": "Data Manager",
  admin: "Clinic Admin",
  owner: "Clinic Owner",
  practitioner: "Clinic Practitioner",
  cro_admin: "CRO Admin",
  Sub_investigator: "Sub Investigator",
  investigator: "Investigator",
  Reviewer: "Reviewer",
};

const AddStaff = ({ setAddStaff, typeOfStaff, PaginationData, paginationurl, toast }) => {
  const navigate = useNavigate();
  const user = useUser();
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const { _id_ } = useParams();
  const { _type_ } = useParams();
  const loaderContext = useContext(LoaderContext);
  const queryClient = useQueryClient();

  const [isUpdate] = useState(_id_ !== undefined);
  const [trials, setTrials] = useState([]);
  const [clinicList, setClinicList] = useState([]);
  const [headUsers, setHeadUsers] = useState([]);
  const [infoData, setInfoData] = useState({
    first_name: "",
    last_name: "",
    email_custom: "",
    isAdmin: false,
    isPractitioner: false,
    clinic: "",
    name: "",
    head_user: "",
  });
  const [selectedTrials, setSelectedTrials] = useState([]);

  const getTrialsList = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    let endpoint;
    if ((role === USER_ROLES.SUPER_ADMIN || role === USER_ROLES.CRO) && infoData?.head_user?.value)
      endpoint = `/api/photos/get_staff_trials/${infoData?.head_user?.value}`;
    else endpoint = "/api/photos/get_staff_trials/";

    axios
      .get(`${__API_URL__}${endpoint}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setTrials(res.data))
      .catch((err) => console.error(err))
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const get_endpoint =
    _type_ === "practitioner"
      ? `practitioners/practitioner/${_id_}/`
      : isUpdate
        ? `clinicAdminView/${_id_}/`
        : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInfoData((prev) => ({ ...prev, [name]: value }));
  };

  const listOfClinics = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/clinic/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        setClinicList(res.data.map((clinic) => ({ label: clinic?.name, value: clinic.id })));
      })
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const addInstance = (e) => {
    e.preventDefault();
    let url;
    if (typeOfStaff === "owner") url = "clinicOwnerCreate/";
    else if (typeOfStaff === "admin") url = "clinicAdminCreate/";
    else if (typeOfStaff === "cro_admin") url = "add_cro/";
    else if (typeOfStaff === "investigator") url = "add_investigator/";
    else if (
      typeOfStaff === "Cro_coordinator" ||
      typeOfStaff === "Data Manager" ||
      typeOfStaff === "Inv_coordinator" ||
      typeOfStaff === "Sub_investigator"
    )
      url = "patient/patient/add_coordinator/";
    else if (typeOfStaff === "Reviewer") url = "practitioners/add_reviewer/";
    else if (typeOfStaff === "practitioner") url = "practitioners/practitioner/";

    const CreateData = {
      ...infoData,
      email: infoData.email_custom,
      clinicName: "",
      clinicAddress: "",
      clinicPhone: "",
      clinic: infoData?.clinic?.["value"],
      trials: selectedTrials.map((trial) => trial.value),
      head_user: infoData?.head_user?.["value"],
      typeOfStaff: typeOfStaff,
    };

    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .post(`${__API_URL__}/api/${url}`, CreateData, {
        headers: { Authorization: `Token ${token}` },
      })
      .then(() => {
        toast.success("Staff Added successfully");
        setAddStaff(false);
        PaginationData(paginationurl);
        queryClient.invalidateQueries({ queryKey: ["trial_logs"] });
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
        toast.error(err.response.data.message);
      })
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const allHeadUsers = () => {
    let url;
    let params = "";
    const isCroScopedInvestigators =
      role === USER_ROLES.CRO &&
      (typeOfStaff === "Inv_coordinator" || typeOfStaff === "Sub_investigator");
    if (typeOfStaff === "Cro_coordinator" || typeOfStaff === "Data Manager")
      url = "get_coordinator_croList";
    else if (typeOfStaff === "Inv_coordinator" || typeOfStaff === "Sub_investigator") {
      url = isCroScopedInvestigators ? "get_investigators" : "add_investigator";
      if (isCroScopedInvestigators) params = "?page_size=200";
    }

    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/${url}/${params}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        const investigators = isCroScopedInvestigators ? res.data.results : res.data;
        setHeadUsers(investigators.map((u) => ({ label: u?.email, value: u.id })));
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      })
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const updateInfoStaff = (instance) => {
    const updateData = {
      first_name: instance.first_name,
      last_name: instance.last_name,
      name: instance.name,
      clinic_id: instance.clinic,
      isPractitioner: instance.isPractitioner,
    };
    axios
      .patch(`${__API_URL__}/api/${get_endpoint}`, updateData, {
        headers: { Authorization: `Token ${token}` },
      })
      .catch((err) => {
        if (err === "Error: Request failed with status code 401") {
          user.actions.logout();
        }
      });
  };

  const updateInstance = (e) => {
    e.preventDefault();
    updateInfoStaff(infoData);
    toast.success("Staff Updated Successfully!");
    setTimeout(() => navigate("/staff/"), 2000);
  };

  const [checkBoxPropsAdmin, setCheckBoxPropsAdmin] = useState({});
  const [checkBoxPropsPrac, setCheckBoxPropsPrac] = useState({});
  const { PatientData } = useFetch(get_endpoint);

  useEffect(() => {
    PatientData &&
      setInfoData({
        ...infoData,
        first_name: PatientData.first_name,
        last_name: PatientData.last_name,
        email_custom: PatientData.user.email,
        clinic:
          _type_ === "practitioner"
            ? PatientData.practitioner_clinic[0]?.id
            : PatientData.clinics[0]?.id,
        name: PatientData.name ? PatientData.name : "",
        isAdmin: PatientData.isAdmin ? PatientData.isAdmin : false,
        isPractitioner: PatientData.isPractitioner ? PatientData.isPractitioner : false,
      });

    PatientData.isAdmin || _type_ === "admin"
      ? setCheckBoxPropsAdmin({
          checked: true,
          disabled: isUpdate && PatientData.isAdmin,
        })
      : setCheckBoxPropsAdmin({
          disabled: isUpdate && PatientData.isAdmin,
        });
    PatientData.isPractitioner || _type_ === "practitioner"
      ? setCheckBoxPropsPrac({
          checked: true,
          disabled: isUpdate && PatientData.isPractitioner,
        })
      : setCheckBoxPropsPrac({
          disabled: isUpdate && PatientData.isPractitioner,
        });
  }, [PatientData]);

  const capitalized_name = STAFF_LABELS[typeOfStaff] || typeOfStaff;

  useEffect(() => {
    listOfClinics();
  }, []);

  useEffect(() => {
    if (
      (role === USER_ROLES.SUPER_ADMIN &&
        (typeOfStaff === "Inv_coordinator" ||
          typeOfStaff === "Sub_investigator" ||
          typeOfStaff === "Cro_coordinator" ||
          typeOfStaff === "Data Manager")) ||
      (role === USER_ROLES.CRO &&
        (typeOfStaff === "Inv_coordinator" || typeOfStaff === "Sub_investigator"))
    ) {
      allHeadUsers();
    }
  }, []);

  useEffect(() => {
    getTrialsList();
  }, [infoData?.head_user]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={isUpdate ? (e) => updateInstance(e) : (e) => addInstance(e)}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" onClick={() => setAddStaff(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {isUpdate ? "Update Staff" : `Add ${capitalized_name}`}
              </Typography>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={loaderContext.loaderCount > 0}
              startIcon={
                loaderContext.loaderCount > 0 ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isUpdate ? "Update" : "Submit"}
            </Button>
          </Box>

          <Stack spacing={2}>
            {isUpdate && _type_ === "practitioner" ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  required
                  label="Name"
                  size="small"
                  fullWidth
                  name="name"
                  value={infoData.name}
                  onChange={handleChange}
                />
              </Stack>
            ) : isUpdate ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  required
                  label="First Name"
                  size="small"
                  fullWidth
                  name="first_name"
                  value={infoData.first_name}
                  onChange={handleChange}
                />
                <TextField
                  required
                  label="Last Name"
                  size="small"
                  fullWidth
                  name="last_name"
                  value={infoData.last_name}
                  onChange={handleChange}
                />
              </Stack>
            ) : null}

            {!isUpdate && (
              <>
                <Stack direction="row" spacing={2}>
                  <TextField
                    required
                    label="First Name"
                    size="small"
                    fullWidth
                    name="first_name"
                    value={infoData.first_name}
                    onChange={handleChange}
                  />
                  <TextField
                    required
                    label="Last Name"
                    size="small"
                    fullWidth
                    name="last_name"
                    value={infoData.last_name}
                    onChange={handleChange}
                  />
                </Stack>

                <Stack direction="row" spacing={2}>
                  <TextField
                    required
                    label="Email Address"
                    size="small"
                    fullWidth
                    type="email"
                    name="email_custom"
                    value={infoData.email_custom}
                    onChange={handleChange}
                  />

                  {(typeOfStaff === "admin" || typeOfStaff === "practitioner") && (
                    <Autocomplete
                      fullWidth
                      value={infoData?.clinic || null}
                      options={clinicList}
                      getOptionLabel={(opt) => opt.label || ""}
                      isOptionEqualToValue={(opt, val) => opt.value === val.value}
                      onChange={(_, newValue) =>
                        setInfoData((prev) => ({ ...prev, clinic: newValue }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Clinic" />
                      )}
                    />
                  )}
                </Stack>
              </>
            )}

            {(typeOfStaff === "Inv_coordinator" ||
              typeOfStaff === "Cro_coordinator" ||
              typeOfStaff === "Data Manager" ||
              typeOfStaff === "Sub_investigator") && (
              <Stack direction="row" spacing={2}>
                {(role === USER_ROLES.SUPER_ADMIN ||
                  (role === USER_ROLES.CRO &&
                    (typeOfStaff === "Inv_coordinator" || typeOfStaff === "Sub_investigator"))) && (
                  <Autocomplete
                    fullWidth
                    value={infoData?.head_user || null}
                    options={headUsers}
                    getOptionLabel={(opt) => opt.label || ""}
                    isOptionEqualToValue={(opt, val) => opt.value === val.value}
                    onChange={(_, newValue) =>
                      setInfoData((prev) => ({ ...prev, head_user: newValue }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label={
                          typeOfStaff === "Inv_coordinator" || typeOfStaff === "Sub_investigator"
                            ? "Investigator"
                            : "CRO Admin"
                        }
                      />
                    )}
                  />
                )}
                <Autocomplete
                  multiple
                  fullWidth
                  value={selectedTrials}
                  options={trials.map((trial) => ({
                    value: trial.id,
                    label: trial.name,
                  }))}
                  getOptionLabel={(opt) => opt.label || ""}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  onChange={(_, newValue) => setSelectedTrials(newValue)}
                  renderInput={(params) => <TextField {...params} size="small" label="Trials" />}
                />
              </Stack>
            )}

            {typeOfStaff === "Reviewer" && (
              <Stack direction="row" spacing={2}>
                <Autocomplete
                  multiple
                  fullWidth
                  value={selectedTrials}
                  options={trials.map((trial) => ({
                    value: trial.id,
                    label: trial.name,
                  }))}
                  getOptionLabel={(opt) => opt.label || ""}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  onChange={(_, newValue) => setSelectedTrials(newValue)}
                  renderInput={(params) => <TextField {...params} size="small" label="Trials" />}
                />
              </Stack>
            )}

            {(typeOfStaff === "admin" ||
              typeOfStaff === "practitioner" ||
              typeOfStaff === "owner") && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                  Roles
                </Typography>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={infoData.isAdmin || false}
                        disabled={checkBoxPropsAdmin.disabled}
                        onChange={() =>
                          setInfoData({
                            ...infoData,
                            isAdmin: !infoData.isAdmin,
                          })
                        }
                      />
                    }
                    label="Clinic Admin"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={infoData.isPractitioner || false}
                        disabled={checkBoxPropsPrac.disabled}
                        onChange={() =>
                          setInfoData({
                            ...infoData,
                            isPractitioner: !infoData.isPractitioner,
                          })
                        }
                      />
                    }
                    label="Clinic Practitioner"
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default AddStaff;
